// ============================================================
// Calculadora de Tarifas — Bryemma
// Modelos de negocio para El Tigre, Venezuela
// Los valores por defecto son sobrescritos desde BD (tariffs_config)
// ============================================================

import { VEHICLE_TYPES, PAYMENT_METHODS } from '../../../constants';
import {
  getActiveTariffRow,
  getPlatformCommissionsRaw,
  getServiceSubcategoryTariff,
} from '../../../services/api/trips-ui';

// ── Valores por defecto (se usan si la BD no responde) ──────────────────────
export const DEFAULT_CONFIG = {
  base_price: 2.0,
  price_per_km: 0.60,
  price_per_minute: 0.15,
  gas_per_km: 0.018,
  weight_factor_light: 1.0,   // < 5 kg
  weight_factor_medium: 1.35, // 5-15 kg
  weight_factor_heavy: 1.65,  // > 15 kg
  min_price: 2.50,
  surge_multiplier: 1.0,
  platform_commission: 15.0,  // porcentaje
  car_multiplier: 1.5,        // factor multiplicador para carros
};


// ── Helper para sanitizar configuraciones con valores nulos ──────────────────
export function sanitizeConfig(config) {
  const safeConfig = { ...DEFAULT_CONFIG };
  if (!config) return safeConfig;
  
  for (const key of Object.keys(DEFAULT_CONFIG)) {
    if (config[key] !== undefined && config[key] !== null) {
      const val = parseFloat(config[key]);
      if (!isNaN(val)) {
        safeConfig[key] = val;
      }
    }
  }
  
  // Normalizar price_per_min (desde subcategorías) a price_per_minute
  if (config.price_per_min !== undefined && config.price_per_min !== null) {
    const val = parseFloat(config.price_per_min);
    if (!isNaN(val)) {
      safeConfig.price_per_minute = val;
    }
  }

  // Preservar configuración de comisiones por método de pago
  if (config.platform_commissions && typeof config.platform_commissions === 'object') {
    safeConfig.platform_commissions = config.platform_commissions;
  }
  
  return safeConfig;
}

// ── Cache en memoria para no hacer múltiples requests a BD ──────────────────
let _cachedConfig = null;
let _cacheTimestamp = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

/**
 * Obtiene la configuración de tarifas desde la BD con cache.
 */
export async function getActiveTariffConfig() {
  const now = Date.now();
  if (_cachedConfig && (now - _cacheTimestamp) < CACHE_TTL_MS) {
    return _cachedConfig;
  }
  try {
    // Fetch tariffs config
    const { data: tariffData, error: tariffError } = await getActiveTariffRow();

    // Fetch platform commissions JSON
    const { data: commData } = await getPlatformCommissionsRaw();

    if (!tariffError && tariffData) {
      let platform_commissions = null;
      if (commData && commData.value) {
        try {
          platform_commissions = JSON.parse(commData.value);
        } catch(e) {}
      }

      const sanitizedData = sanitizeConfig(tariffData);
      _cachedConfig = { 
        ...sanitizedData, 
        platform_commissions 
      };
      _cacheTimestamp = now;
      return _cachedConfig;
    }
  } catch (_) {
    // Sin conexión, usar defaults
  }
  return DEFAULT_CONFIG;
}

/** Invalida el cache (llamar tras guardar nuevas tarifas en AdminPanel) */
export function invalidateTariffCache() {
  _cachedConfig = null;
  _cacheTimestamp = 0;
}

// ── Cálculos de distancia (Haversine) ───────────────────────────────────────

/**
 * Distancia entre dos coordenadas GPS (Haversine)
 * @returns {number} Distancia en km
 */
export { calcularDistancia } from '../../../utils/geo';

/**
 * Estima tiempo de recorrido en El Tigre (moto en zona urbana)
 * @param {number} distanciaKm
 * @returns {number} Minutos estimados
 */
export const estimarTiempo = (distanciaKm) => {
  const VELOCIDAD_PROMEDIO_KMH = 25; // moto en zona urbana
  return Math.ceil((distanciaKm / VELOCIDAD_PROMEDIO_KMH) * 60);
};

// ── Factor de peso ───────────────────────────────────────────────────────────

/**
 * Calcula el factor de peso según los rangos configurados
 * @param {number} pesoKg  
 * @param {object} config — configuración de tarifas
 */
export function getPesoFactor(pesoKg, config = DEFAULT_CONFIG) {
  const safeConfig = sanitizeConfig(config);
  if (pesoKg > 15) return safeConfig.weight_factor_heavy;
  if (pesoKg > 5)  return safeConfig.weight_factor_medium;
  return safeConfig.weight_factor_light;
}

// ── Factor de comisión según método de pago ─────────────────────────────────

/**
 * Obtiene la tasa de comisión para un método de pago específico.
 * Utiliza platform_commissions si está disponible, o un fallback.
 */
export function getPaymentMethodCommission(paymentMethod, config = DEFAULT_CONFIG) {
  const method = paymentMethod?.toLowerCase()?.replace(' ', '_') || PAYMENT_METHODS.TRANSFERENCIA;
  let key = PAYMENT_METHODS.TRANSFERENCIA;
  if (method.includes('pago_movil') || method.includes('movil')) key = PAYMENT_METHODS.PAGO_MOVIL;
  else if (method.includes('binance')) key = PAYMENT_METHODS.BINANCE;
  else if (method.includes('divisas') || method.includes('efectivo')) key = PAYMENT_METHODS.DIVISAS;

  // Si existe la configuración de comisiones JSON, úsala
  if (config.platform_commissions && config.platform_commissions[key] !== undefined) {
    return Number(config.platform_commissions[key]) / 100;
  }
  
  // Fallback a la tasa genérica
  return (Number(config.platform_commission) || 15) / 100;
}

// ── Cálculo principal de tarifa ──────────────────────────────────────────────

/**
 * Calcula la tarifa completa de un servicio.
 * 
 * @param {number} distanciaKm       — Distancia GPS calculada
 * @param {number} pesoKg            — Peso del paquete (0 para ride/viaje)
 * @param {number} tiempoEstimadoMin — Tiempo estimado en minutos
 * @param {object} config            — Config desde BD (o DEFAULT_CONFIG como fallback)
 * @param {string} vehicleType       — Tipo de vehículo (VEHICLE_TYPES.MOTO o VEHICLE_TYPES.CARRO)
 * @param {string} paymentMethod     — Método de pago (opcional, para comisión específica)
 * 
 * @returns {{ total, commission, driverPayout, breakdown, surgeActive }}
 */
export const calcularTarifa = (distanciaKm, pesoKg = 0, tiempoEstimadoMin = 0, config = DEFAULT_CONFIG, vehicleType = VEHICLE_TYPES.MOTO, paymentMethod = null) => {
  const safeConfig = sanitizeConfig(config);
  const {
    base_price,
    price_per_km,
    price_per_minute,
    gas_per_km,
    min_price,
    surge_multiplier,
    platform_commission,
    car_multiplier = 1.5,
  } = safeConfig;

  // Componentes de costo
  const costoDistancia = distanciaKm * price_per_km;
  const costoTiempo    = tiempoEstimadoMin * price_per_minute;
  const costoGasolina  = distanciaKm * gas_per_km;
  const subtotal       = base_price + costoDistancia + costoTiempo + costoGasolina;

  // Factor de peso (solo delivery)
  const factorPeso = getPesoFactor(pesoKg, safeConfig);

  // Subtotal con peso
  let total = subtotal * factorPeso;

  // Multiplicador de carro (normalizar 'car' a 'carro' para compatibilidad)
  const normalizedType = vehicleType === 'car' ? VEHICLE_TYPES.CARRO : vehicleType;
  const vehicleMultiplier = normalizedType === VEHICLE_TYPES.CARRO ? car_multiplier : 1.0;
  total = total * vehicleMultiplier;

  // Aplicar surge multiplier (demanda alta)
  const surgeActive = surge_multiplier > 1.0;
  total = total * (surge_multiplier || 1.0);

  // Precio mínimo garantizado
  total = Math.max(total, min_price);

  // Redondear a múltiplos de $0.50
  total = Math.ceil(total / 0.5) * 0.5;

  // Comisión de la plataforma
  const commissionRate = paymentMethod 
    ? getPaymentMethodCommission(paymentMethod, safeConfig) 
    : (safeConfig.platform_commission || 15) / 100;
    
  const commission   = total * commissionRate;
  const driverPayout = total - commission;

  return {
    total:       parseFloat(total.toFixed(2)),
    commission:  parseFloat(commission.toFixed(2)),
    driverPayout: parseFloat(driverPayout.toFixed(2)),
    surgeActive,
    surgeMultiplier: surge_multiplier || 1.0,
    vehicleMultiplier,
    breakdown: {
      base:       parseFloat(base_price.toFixed(2)),
      distancia:  parseFloat(costoDistancia.toFixed(2)),
      tiempo:     parseFloat(costoTiempo.toFixed(2)),
      gasolina:   parseFloat(costoGasolina.toFixed(2)),
      factorPeso,
      vehicleMultiplier,
      surge:      surgeActive ? parseFloat((total - (total / (surge_multiplier || 1))).toFixed(2)) : 0,
    },
  };
};

/**
 * Obtiene tarifas desde una service_subcategory por ID
 */
export async function getSubcategoryTariff(subcategoryId) {
  try {
    const { data } = await getServiceSubcategoryTariff(subcategoryId);
    return data;
  } catch (_) {
    return null;
  }
}
